import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { api, formatApiErrorDetail, setAuthToken, clearAuthToken } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [checked, setChecked] = useState(false);

  // Bumped on login/register/logout so a stale in-flight /auth/me can't overwrite newer auth state.
  const epoch = useRef(0);

  const refreshMe = useCallback(async () => {
    const mine = epoch.current;
    try {
      const { data } = await api.get('/auth/me');
      if (mine === epoch.current) setUser(data);
    } catch {
      if (mine !== epoch.current) return;
      clearAuthToken();
      setUser(false);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password });
      epoch.current += 1;
      if (data?.access_token) setAuthToken(data.access_token);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post('/auth/register', payload);
      epoch.current += 1;
      if (data?.access_token) setAuthToken(data.access_token);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {}
    epoch.current += 1;
    clearAuthToken();
    setUser(false);
  };

  // Request a reset link. Always resolves ok (backend returns a neutral response
  // regardless of whether the email exists, to avoid leaking registered emails).
  const requestPasswordReset = async (email) => {
    try {
      await api.post('/auth/forgot-password', { email });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const resetPassword = async (token, new_password) => {
    try {
      await api.post('/auth/reset-password', { token, new_password });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  return (
    <AuthContext.Provider value={{ user, checked, login, register, logout, refreshMe, requestPasswordReset, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
